import { OverlayRef } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
  Optional
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LifePlanInsurances } from '@core/models/insurance.model';
import { LifePlan } from '@core/models/life-plan.model';
import {
  DeathMenuProtection,
  EducationMenuProtection,
  MenuProtection,
  RetirementMenuProtection,
  SavingMenuProtection,
  DecumulationMenuProtection
} from '@core/models/menu-protection.model';
import { StorageService } from '@core/services/core/storage.service';
import { LoginService } from '@core/services/login.service';
import { UiService } from '@core/services/ui.service';
import { Store } from '@ngrx/store';
import { AppState } from '@redux/app.reducers';
import { RatingsState } from '@redux/reducers';
import { compile, match } from 'path-to-regexp';
import { Subscription } from 'rxjs';

interface PathMath {
  lifePlanId: string | null;
}

export interface MenuConfig {
  view: 'agent';
  lifePlanId: string;
  disabled: boolean;
  opened: boolean;
  protections: MenuProtection[];
}

export const MENU_CONFIG_TOKEN = new InjectionToken<MenuConfig>('MENU_CONFIG');

@Component({
  selector: 'mfp-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy {
  @Input() view = 'agent';
  @Input() disabled = false;
  @Input() opened = false;
  @Input() onlyLifePlanNavigation = false;
  @Input() lifePlanId = '';
  @Input() protections = [
    { ...DeathMenuProtection },
    { ...EducationMenuProtection },
    { ...SavingMenuProtection },
    { ...RetirementMenuProtection },
    { ...DecumulationMenuProtection }
  ];

  loggedUserName = '';
  selectedNavigation: 'menu' | 'life-plan' = 'menu';
  window = window;
  retirementError! : boolean;

  currentLifePlan?: LifePlan;
  ratingsState?: RatingsState;
  $storeSubscription: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private loginService: LoginService,
    @Optional() @Inject(MENU_CONFIG_TOKEN) private menuConfig: MenuConfig,
    @Optional() private overlayRef: OverlayRef,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private storageService: StorageService,
    private uiService: UiService
  ) {
    this.$storeSubscription = this.store.subscribe({
      next: state => {
        this.currentLifePlan = state.lifePlan.currentLifePlan;
        this.ratingsState = state.ratings;
        this.retirementError = state.retirementInfo.error;
        this.protections = this.retrieveMenuProtectionsFromInsurances(this.currentLifePlan.insurances);
        this.loggedUserName = state.agentData.agentName;
      }
    });
    // TODO: get this from redux or make something with menuConfig
    this.view = 'agent';
    if (this.overlayRef !== null) {
      this.opened = true;
    } else {
      // TODO: store this.opened in Redux store and remove this once done
      this.route.queryParamMap.subscribe({
        next: params => {
          const openedParam = params.get('menuOpened');
          this.opened = openedParam === 'true';
        }
      });
    }
    if (menuConfig) {
      // TODO: remove this fields if they can be get from redux
      this.view = menuConfig.view || this.view;
      this.opened = menuConfig.opened || this.opened;
      this.disabled = menuConfig.disabled || this.disabled;
    }
    if (window.innerWidth > 1920) {
      this.opened = true;
    }
    window.addEventListener('resize', this.onResize);
  }

  private onResize = () => {
    if (window.innerWidth > 1920) {
      this.opened = true;
    }
    this.changeDetectorRef.detectChanges();
  };

  ngOnInit(): void {
    switch (this.currentLifePlan?.status) {
      case 'DRAFT':
        this.selectedNavigation = 'menu';
        break;
      default:
        this.selectedNavigation = 'life-plan';
        break;
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.onResize);
    this.$storeSubscription.unsubscribe();
  }

  onMenuButtonClick() {
    this.opened = true;
  }

  onCloseButtonClick() {
    if (this.overlayRef !== null) {
      this.overlayRef.dispose();
    } else {
      this.opened = false;
    }
  }

  onBackButtonClick() {
    if (!this.onlyLifePlanNavigation) {
      this.selectedNavigation = 'menu';
    }
  }

  getTotalCartProducts(): number {
    return this.protections.map(p => p.productsAdded).reduce((sum, current) => sum + current, 0);
  }

  getTotalFinancialHealth(): number {
    return this.protections.map(p => p.healthPoints).reduce((sum, current) => sum + current, 0);
  }

  checkItemActive(path: string): boolean {
    const matching = match(path, { decode: decodeURIComponent });
    return matching(this.router.url.split('?')[0]) !== false;
  }

  checkItemsActive(paths: string[]): boolean {
    return paths.map(p => this.checkItemActive(p)).find(v => v) || false;
  }

  navigateTo(path: string): void {
    const toPath = compile(path, { encode: encodeURIComponent });
    const finalPath = toPath({
      userType: this.view,
      lifePlanId: this.currentLifePlan?.id
    });
    this.router.navigate([finalPath], {
      // TODO: store this.opened in Redux store and remove this once done
      queryParams: { menuOpened: this.opened }
    });
    if (this.overlayRef !== null) {
      this.overlayRef.dispose();
    }
  }

  logout(): void {
    this.loginService.onLogout();
    this.navigateTo('/login');
  }

  retrieveMenuProtectionsFromInsurances(lifePlanInsurances?: LifePlanInsurances) {
    const protections: MenuProtection[] = [
      { ...DeathMenuProtection },
      { ...EducationMenuProtection },
      { ...SavingMenuProtection },
      { ...RetirementMenuProtection },
      { ...DecumulationMenuProtection }
    ];
    if (lifePlanInsurances) {
      Object.entries(lifePlanInsurances).forEach(([id, insuranceTarget]) => {
        const protection = protections.find(p => p.insurance === insuranceTarget.insurance);
        if (protection && insuranceTarget.currentProduct) {
          if(protection.insurance === "RETIREMENT" && this.retirementError) {
            protection.active = false;
          } else {
            protection.active = true;
          }
          protection.totalProducts = protection.totalProducts + 1;
          if (insuranceTarget.currentProduct.cartStatus.added) {
            protection.productsAdded = protection.productsAdded + 1;
            const rating = this.ratingsState?.insuranceTargets[insuranceTarget.id];
            if (rating) {
              protection.healthPoints = protection.healthPoints + (rating.current - rating.default);
            }
          }
        }
      });
    }
    return protections;
  }
}
